import React, { useEffect, useState } from "react";
import axios from 'axios';
import { Pencil, Trash2, UserCheck, UserX, Image as ImageIcon, User, ChevronLeft, ChevronRight, XCircle, Lock } from 'lucide-react';
import { format, addMonths, subMonths, isValid } from 'date-fns';
import StudentPhotoModal from '../components/modalComponents/StudentPhotoModal'

export default function Students() {
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 16;

  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({});
  const [hideInactive, setHideInactive] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');

  // Attendance modal states
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  //password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedStudentForPw, setSelectedStudentForPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  //filter
  const [sortConfig, setSortConfig] = useState({ key: 'c_time', direction: 'desc' });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`);
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch students.');
    }
  };

  const fetchStudentAttendance = async (studentId, month) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentAttendanceByMonth`, {
        params: { student_id: studentId, month },
      });
      if (response.data.success) {
        setAttendanceRecords(response.data.data || []);
      } else {
        setAttendanceRecords([]);
      }
    } catch (error) {
      console.error('Error fetching student attendance:', error);
      alert('Failed to fetch attendance records.');
      setAttendanceRecords([]);
    }
  };

  const openAttendanceModal = (student) => {
    setSelectedStudent(student);
    setCurrentMonth(format(new Date(), 'yyyy-MM'));
    fetchStudentAttendance(student.student_id, currentMonth);
    setShowAttendanceModal(true);
  };

  const closeAttendanceModal = () => {
    setShowAttendanceModal(false);
    setSelectedStudent(null);
    setAttendanceRecords([]);
    setCurrentMonth(format(new Date(), 'yyyy-MM'));
  };

  const handleMonthChange = (direction) => {
    const monthDate = new Date(`${currentMonth}-01`);
    const newMonthDate = direction === 'next' 
      ? addMonths(monthDate, 1)
      : subMonths(monthDate, 1);
    const newMonth = format(newMonthDate, 'yyyy-MM');
    setCurrentMonth(newMonth);
    if (selectedStudent) {
      fetchStudentAttendance(selectedStudent.student_id, newMonth);
    }
  };

  const handleMonthSelect = (e) => {
    const newMonth = e.target.value;
    setCurrentMonth(newMonth);
    if (selectedStudent) {
      fetchStudentAttendance(selectedStudent.student_id, newMonth);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1); // reset to page 1 when searching
  };

  const filteredStudents = students.filter((s) => {
    const matchesSearch = `${s.student_id} ${s.student_login_id} ${s.first_name} ${s.surname} ${s.chinese_name} ${s.phone} ${s.parent_phone} ${s.school}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  
    const isActive = s.status !== 'inactive'; // or whatever your inactive value is
  
    return hideInactive ? matchesSearch && isActive : matchesSearch;
  });  

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    const { key, direction } = sortConfig;

    let aValue = a[key];
    let bValue = b[key];

    // Handle null / undefined
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Special handling for date
    if (key === 'c_time') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Special handling for student_id (treat as number if possible)
    if (key === 'student_id') {
      aValue = isNaN(aValue) ? aValue : Number(aValue);
      bValue = isNaN(bValue) ? bValue : Number(bValue);
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const indexOfLast = currentPage * studentsPerPage;
  const indexOfFirst = indexOfLast - studentsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirst, indexOfLast);  // ← change here
  const totalPages = Math.ceil(sortedStudents.length / studentsPerPage);    // ← and here

  const handleDelete = async (student_id) => {
    if (window.confirm("Are you sure to delete this student?")) {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deletestudent/${student_id}`);
      fetchStudents();
    }
  };

  const handleToggleStatus = async (student_id) => {
    await axios.patch(`${process.env.REACT_APP_API_URL}/admin/toggleStudentStatus/${student_id}`);
    fetchStudents();
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({ ...student });
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async () => {
    try {
      if (!editingStudent || !editingStudent.student_id) {
        alert("Missing student ID.");
        return;
      }
  
      await axios.put(`${process.env.REACT_APP_API_URL}/admin/updatestudent/${editingStudent.student_id}`, formData);
      
      alert("Student updated successfully.");
      setEditingStudent(null);
      fetchStudents();
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update student. Please try again.");
    }
  };

  // Attendance toggle functions
  const toggleAttendance = (index) => {
    const newRecords = [...attendanceRecords];
    const current = newRecords[index].attendance;
    newRecords[index].attendance =
      current === 'pending' ? 'presence' :
      current === 'presence' ? 'absence' :
      current === 'absence' ? 'leave' :
      'pending';
    setAttendanceRecords(newRecords);
    updateAttendanceRecord(newRecords[index]);
  };

  const toggleHomeworkStatus = (index) => {
    const newRecords = [...attendanceRecords];
    const current = newRecords[index].homework_status;
    newRecords[index].homework_status = current === 0 ? 1 : current === 1 ? 2 : 0;
    setAttendanceRecords(newRecords);
    updateAttendanceRecord(newRecords[index]);
  };

  const updateAttendanceRecord = async (record) => {
    if (!record.live_lesson_id) {
      console.error('No live_lesson_id available for update.');
      return;
    }
    setLoadingAttendance(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/updateAttendanceRecord`, {
        live_lesson_id: record.live_lesson_id,
        results: [{
          student_id: selectedStudent.student_id,
          attendance: record.attendance || 'pending',
          homework_status: Number(record.homework_status) || 0,
        }],
      });
    } catch (error) {
      console.error('Error updating attendance:', error);
      alert('Failed to update attendance record.');
      // Optionally refetch to revert
      fetchStudentAttendance(selectedStudent.student_id, currentMonth);
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Render visual indicators
  const renderAttendanceIndicator = (attendance) => {
    if (attendance === 'presence') return '✓';
    if (attendance === 'absence') return '✗';
    if (attendance === 'leave') return 'L';
    return '';  // For 'pending'
  };

  const renderHomeworkIndicator = (status) => {
    if (status === 1) return '✓';
    if (status === 2) return '✗';
    return '';
  };

  const openPasswordResetModal = (student) => {
    setSelectedStudentForPw(student);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };
  
  const handlePasswordReset = async () => {
    if (!newPassword || !confirmPassword) {
      setPasswordError('Both fields are required');
      return;
    }
  
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
  
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
  
    // I put this route in app route as i dont want to set up other route file under admin route
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/resetStudentPassword/${selectedStudentForPw.student_id}`,
        { newPassword }
      );
  
      alert('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      console.error('Password reset failed:', error);
      const msg = error.response?.data?.error || 'Failed to reset password';
      setPasswordError(msg);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1); // optional: go back to page 1 when sorting
  };

  return (
      <div className="p-8 min-h-screen overflow-y-auto">
        <h1 className="text-2xl font-bold">Students</h1>
        <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>

        {/* Search Bar */}
        <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
          <input
            type="text"
            placeholder="Search by name, ID login ID, phone, school..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <label className="flex items-center mt-2 md:mt-0">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="mr-2"
            />
            Hide Inactive Students
          </label>
        </div>

        <div className="overflow-x-auto bg-neutral-100 rounded-md">
          <table className="min-w-full bg-white border-collapse">
            <thead>
              <tr className="bg-gray-200 text-sm text-left">
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('student_id')}
                >
                  Student ID {sortConfig.key === 'student_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="border px-4 py-2">Student Login ID</th>
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('surname')}
                >
                  Surname {sortConfig.key === 'surname' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('first_name')}
                >
                  First Name {sortConfig.key === 'first_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="border px-4 py-2">Chinese Name</th>
                <th className="border px-4 py-2">Phone</th>
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('school')}
                >
                  School {sortConfig.key === 'school' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('grade')}
                >
                  Grade {sortConfig.key === 'grade' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border px-4 py-2 cursor-pointer hover:bg-gray-300 select-none"
                  onClick={() => handleSort('c_time')}
                >
                  Registration Date {sortConfig.key === 'c_time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="border px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentStudents.map((student) => (
                <tr key={student.student_id} className="text-center border-t hover:bg-gray-50 transition">
                  <td className="px-4 py-2 border">{student.student_id}</td>
                  <td className="px-4 py-2 border">{student.student_login_id}</td>
                  <td className="px-4 py-2 border">{student.surname}</td>
                  <td className="px-4 py-2 border">{student.first_name}</td>
                  <td className="px-4 py-2 border">{student.chinese_name}</td>
                  <td className="px-4 py-2 border">{student.phone}</td>
                  <td className="px-4 py-2 border">{student.school}</td>
                  <td className="px-4 py-2 border">{student.grade}</td>
                  <td className="px-4 py-2 border">{student.c_time ? new Date(student.c_time).toISOString().split('T')[0] : '-'}</td>
                  <td className="px-4 py-2 border flex justify-center gap-2">
                    <button className="text-blue-600 hover:text-blue-800"  onClick={() => handleEdit(student)}>
                      <Pencil size={18} />
                    </button>
                    <button className="text-red-600 hover:text-red-800" onClick={() => handleDelete(student.student_id)}>
                      <Trash2 size={18} />
                    </button>
                    <button
                      className="text-green-600 hover:text-green-800"
                      onClick={() => {
                        setSelectedPhotoUrl(student.student_photo);
                        setShowPhotoModal(true);
                      }}
                    >
                      <ImageIcon size={18} />
                    </button>
                    <button
                      className={`hover:text-green-800 ${
                        student.status === 'active' ? 'text-yellow-600' : 'text-green-600'
                      }`}
                      onClick={() => handleToggleStatus(student.student_id)}
                    >
                      {student.status === 'active' ? <UserX size={18} /> : <UserCheck size={18} />}
                    </button>
                    <button
                      className="text-purple-600 hover:text-purple-800"
                      onClick={() => openAttendanceModal(student)}
                    >
                      <User size={18} />
                    </button>
                    <button
                      className="text-indigo-600 hover:text-indigo-800"
                      onClick={() => openPasswordResetModal(student)}
                      title="Reset Password"
                    >
                      <Lock size={18} /> {/* or Key, RefreshCw, etc. – choose what you prefer */}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <StudentPhotoModal
          isOpen={showPhotoModal}
          onClose={() => setShowPhotoModal(false)}
          photoUrl={selectedPhotoUrl}
        />
        
        {/* Edit Modal */}
        {editingStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-semibold">Edit Student</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Surname</label>
                  <input name="surname" placeholder="Surname" value={formData.surname || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input name="first_name" placeholder="First Name" value={formData.first_name || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input name="phone" placeholder="Phone" value={formData.phone || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Grade</label>
                  <input name="grade" placeholder="Grade" value={formData.grade || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">School</label>
                  <input name="school" placeholder="School" value={formData.school || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Chinese Name</label>
                  <input name="chinese_name" placeholder="Chinese Name" value={formData.chinese_name || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Parent Name</label>
                  <input name="parent_name" placeholder="Parent Name" value={formData.parent_name || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Relationship</label>
                  <input name="relationship" placeholder="Relationship" value={formData.relationship || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Parent Phone</label>
                  <input name="parent_phone" placeholder="Parent Phone" value={formData.parent_phone || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <input name="address" placeholder="Address" value={formData.address || ''} onChange={handleFormChange} className="w-full p-2 border rounded" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setEditingStudent(null)} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
                <button onClick={handleFormSubmit} className="bg-blue-600 text-white px-4 py-2 rounded">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Student Attendance Modal */}
        {showAttendanceModal && selectedStudent && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">
                  Attendance for {selectedStudent.surname} {selectedStudent.first_name} (ID: {selectedStudent.student_id}) - {currentMonth}
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMonthChange('prev')}
                      className="p-1 rounded hover:bg-gray-200"
                      disabled={loadingAttendance}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <input
                      type="month"
                      value={currentMonth}
                      onChange={handleMonthSelect}
                      disabled={loadingAttendance}
                      className="p-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                    />
                  </div>
                  <button
                    onClick={() => handleMonthChange('next')}
                    className="p-1 rounded hover:bg-gray-200"
                    disabled={loadingAttendance}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={closeAttendanceModal}
                    className="text-gray-500 hover:text-gray-700"
                    disabled={loadingAttendance}
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96">
                {attendanceRecords.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No attendance records for this month.</p>
                ) : (
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-purple-100 sticky top-0">
                        <th className="px-4 py-2 border">Date</th>
                        <th className="px-4 py-2 border">Attendance</th>
                        <th className="px-4 py-2 border">Homework Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceRecords.map((record, index) => {
                        const parsedDate = record.date ? new Date(record.date) : null;
                        const recordDate = parsedDate && isValid(parsedDate) ? format(parsedDate, 'MMM d, yyyy') : 'Invalid Date';
                        return (
                          <tr key={`${record.live_lesson_id}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 border">{recordDate}</td>
                            <td className="px-4 py-2 border text-center">
                              <button
                                type="button"
                                onClick={() => toggleAttendance(index)}
                                disabled={loadingAttendance}
                                className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                              >
                                {renderAttendanceIndicator(record.attendance)}
                              </button>
                            </td>
                            <td className="px-4 py-2 border text-center">
                              <button
                                type="button"
                                onClick={() => toggleHomeworkStatus(index)}
                                disabled={loadingAttendance}
                                className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                              >
                                {renderHomeworkIndicator(record.homework_status)}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {loadingAttendance && <p className="text-purple-600 text-center mt-2">Updating...</p>}
            </div>
          </div>
        )}

        {showPasswordModal && selectedStudentForPw && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Reset Password – {selectedStudentForPw.surname} {selectedStudentForPw.first_name}
                </h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Re-enter password"
                  />
                </div>

                {passwordError && (
                  <p className="text-red-600 text-sm">{passwordError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <select
            value={currentPage}
            onChange={(e) => setCurrentPage(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <option key={page} value={page}>
                Page {page} of {totalPages}
              </option>
            ))}
          </select>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
}